// Multilingual support for master data.
//
// Mirrors the field-level model in `formTranslations.js`, extended with the one
// thing forms do not have: the *rows*. A master table therefore carries
// translations at three levels, each keyed by language code:
//
//   {
//     dataName: "Vendors",                       // default (English)
//     translations: { japanese: { dataName: "取引先" } },
//
//     fields: [                                  // column definitions —
//       { id, label: "Vendor Name", ... ,        // translated by
//         translations: { japanese: { label: "取引先名" } } },   // formTranslations.js
//     ],
//
//     data: [
//       {
//         uuid: "row-1",
//         "Vendor Name": "Kestrel Logistics",    // canonical, stays the data key
//         "Address": { "City": "Munich" },       // grouped column
//         translations: {
//           japanese: {
//             "Vendor Name": "ケストレル物流",
//             "Address": { "City": "ミュンヘン" },
//           },
//         },
//       },
//     ],
//   }
//
// Row translations are keyed by the *canonical* column label, because that is
// already how a row itself is keyed everywhere (`row[field.label]`). Only
// columns whose stored value IS the display text are translated; a dropdown
// stores an option id and a referenced column stores a row uuid, so their text
// lives on the field definition and is handled by `formTranslations`.
//
// Resolution is display-only: the canonical value is never rewritten, so
// requests (which store the row uuid) are untouched and no migration is needed.
// Tables saved before this feature simply have no `translations` key and
// resolve to their default values.

import {
    DEFAULT_LANGUAGE,
    FORM_LANGUAGES,
    FORM_LANGUAGE_OPTIONS,
    resolveFieldTranslation,
} from "./formTranslations.js";

export { DEFAULT_LANGUAGE, FORM_LANGUAGES, FORM_LANGUAGE_OPTIONS, resolveFieldTranslation };

// Master data reuses the app-wide language list so a table, its columns and
// the widgets bound to it all resolve from the same locale.
export const MASTER_LANGUAGES = FORM_LANGUAGES;

// Column types whose stored row value is itself the display text. Everything
// else either holds an identifier (dropdown option ids, referenced row uuids,
// user emails) or is language neutral (numbers, dates, switches).
const ROW_TEXT_TYPES = new Set(["inputForm", "textAreaForm", "textArea"]);

const isPlainObject = (value) =>
    value !== null && typeof value === "object" && !Array.isArray(value);

const isFilledString = (value) => typeof value === "string" && value !== "";

// A column that points at another table stores that table's row uuid, so its
// text must be resolved from the referenced table, never translated in place.
export const isRowTranslatableField = (field) => {
    if (!field || field.parentTable) return false;
    return ROW_TEXT_TYPES.has(field.formType);
};

// Grouped columns (sections) store their values nested under the group label,
// so their children are translated one level down. `buildColumns` keys off the
// presence of `children` rather than the form type, so match that here.
export const getFieldChildren = (field) =>
    Array.isArray(field?.children) && field.children.length > 0 ? field.children : null;

export const hasTranslatableColumns = (fields = []) =>
    (Array.isArray(fields) ? fields : []).some((field) => {
        const children = getFieldChildren(field);
        if (children) return children.some(isRowTranslatableField);
        return isRowTranslatableField(field);
    });

export const resolveMasterNameTranslation = (master, lng) => {
    if (!master) return "";
    if (!lng || lng === DEFAULT_LANGUAGE) return master.dataName;
    const translated = master?.translations?.[lng]?.dataName;
    return isFilledString(translated) ? translated : master.dataName;
};

// Merge a table-name translation onto a master document without touching the
// canonical `dataName`, which is still the identifier used in URLs and lookups.
export const mergeMasterNameTranslation = (master, lng, dataName) => {
    if (!master || !lng || lng === DEFAULT_LANGUAGE) return master;
    return {
        ...master,
        translations: {
            ...(isPlainObject(master.translations) ? master.translations : {}),
            [lng]: {
                ...(isPlainObject(master.translations?.[lng]) ? master.translations[lng] : {}),
                dataName: typeof dataName === "string" ? dataName : "",
            },
        },
    };
};

// `resolveFieldTranslation` covers a field's own text and its `options`, but a
// master column can also carry `children`, which need the same treatment.
export const resolveMasterFieldTranslation = (field, lng) => {
    const resolved = resolveFieldTranslation(field, lng);
    const children = getFieldChildren(field);
    if (!children) return resolved;
    const localizedChildren = children.map((child) => resolveFieldTranslation(child, lng));
    if (localizedChildren.every((child, index) => child === children[index])) return resolved;
    return { ...resolved, children: localizedChildren };
};

export const resolveMasterFieldsTranslation = (fields = [], lng) => {
    if (!Array.isArray(fields)) return [];
    if (!lng || lng === DEFAULT_LANGUAGE) return fields;
    const localized = fields.map((field) => resolveMasterFieldTranslation(field, lng));
    return localized.every((field, index) => field === fields[index]) ? fields : localized;
};

// Merge a row's stored translations onto it for rendering. Fallback is per key,
// not per row: an untranslated column keeps its canonical value while its
// neighbours are translated. Returns the original reference when nothing
// changed so memoised renderers stay stable.
export const resolveRowTranslation = (row, fields = [], lng) => {
    if (!isPlainObject(row) || !lng || lng === DEFAULT_LANGUAGE) return row;
    const overrides = row?.translations?.[lng];
    if (!isPlainObject(overrides)) return row;

    let result = row;
    const ensureCopy = () => {
        if (result === row) result = { ...row };
    };

    (Array.isArray(fields) ? fields : []).forEach((field) => {
        const children = getFieldChildren(field);
        if (children) {
            const groupValue = row[field.label];
            const groupOverride = overrides[field.label];
            if (!isPlainObject(groupValue) || !isPlainObject(groupOverride)) return;
            let group = groupValue;
            children.forEach((child) => {
                if (!isRowTranslatableField(child)) return;
                const text = groupOverride[child.label];
                if (!isFilledString(text) || group[child.label] === text) return;
                if (group === groupValue) group = { ...groupValue };
                group[child.label] = text;
            });
            if (group !== groupValue) {
                ensureCopy();
                result[field.label] = group;
            }
            return;
        }
        if (!isRowTranslatableField(field)) return;
        const text = overrides[field.label];
        if (!isFilledString(text) || row[field.label] === text) return;
        ensureCopy();
        result[field.label] = text;
    });

    return result;
};

export const resolveRowsTranslation = (rows = [], fields = [], lng) => {
    if (!Array.isArray(rows)) return [];
    if (!lng || lng === DEFAULT_LANGUAGE) return rows;
    const localized = rows.map((row) => resolveRowTranslation(row, fields, lng));
    return localized.every((row, index) => row === rows[index]) ? rows : localized;
};

// Pull the translatable text out of the editor's form values. Empty strings are
// kept so an author can clear a bad translation; `undefined` (a control that
// never rendered) is skipped so a stored translation is not silently wiped.
export const extractRowTranslatableValues = (values = {}, fields = []) => {
    const result = {};
    if (!isPlainObject(values)) return result;

    (Array.isArray(fields) ? fields : []).forEach((field) => {
        const children = getFieldChildren(field);
        if (children) {
            const groupValue = values[field.label];
            if (!isPlainObject(groupValue)) return;
            const group = {};
            children.forEach((child) => {
                if (!isRowTranslatableField(child)) return;
                const text = groupValue[child.label];
                if (text === undefined) return;
                group[child.label] = typeof text === "string" ? text : String(text ?? "");
            });
            if (Object.keys(group).length > 0) result[field.label] = group;
            return;
        }
        if (!isRowTranslatableField(field)) return;
        const text = values[field.label];
        if (text === undefined) return;
        result[field.label] = typeof text === "string" ? text : String(text ?? "");
    });

    return result;
};

// Values to load into the editor when translating a row into `lng`: only the
// translatable columns, carrying their stored translation or blank when
// untranslated. Canonical values are shown as reference text alongside, not
// loaded into the inputs, so a translator can never overwrite them.
export const buildRowTranslationDraft = (row, fields = [], lng) => {
    const overrides = isPlainObject(row?.translations?.[lng]) ? row.translations[lng] : {};
    const result = {};

    (Array.isArray(fields) ? fields : []).forEach((field) => {
        const children = getFieldChildren(field);
        if (children) {
            const groupOverride = isPlainObject(overrides[field.label]) ? overrides[field.label] : {};
            const group = {};
            children.forEach((child) => {
                if (!isRowTranslatableField(child)) return;
                group[child.label] = typeof groupOverride[child.label] === "string"
                    ? groupOverride[child.label]
                    : "";
            });
            if (Object.keys(group).length > 0) result[field.label] = group;
            return;
        }
        if (!isRowTranslatableField(field)) return;
        result[field.label] = typeof overrides[field.label] === "string" ? overrides[field.label] : "";
    });

    return result;
};

// Merge the editor's values into the row's translation bucket. Merging — rather
// than replacing — preserves translations for columns the editor did not render,
// while an explicit empty string still clears one.
export const mergeRowTranslations = (row, fields = [], lng, values = {}) => {
    if (!isPlainObject(row) || !lng || lng === DEFAULT_LANGUAGE) return row;

    const edited = extractRowTranslatableValues(values, fields);
    const previous = isPlainObject(row?.translations?.[lng]) ? row.translations[lng] : {};
    const bucket = { ...previous };

    Object.entries(edited).forEach(([key, value]) => {
        if (isPlainObject(value)) {
            bucket[key] = {
                ...(isPlainObject(previous[key]) ? previous[key] : {}),
                ...value,
            };
            return;
        }
        bucket[key] = value;
    });

    return {
        ...row,
        translations: {
            ...(isPlainObject(row.translations) ? row.translations : {}),
            [lng]: bucket,
        },
    };
};

const FIELD_TEXT_KEYS = ["label", "placeholder", "helpText", "description"];
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const isIdLike = (value) => typeof value === "string" && UUID_PATTERN.test(value.trim())

const pickReadableOptionLabel = (options, key) => {
    if (!Array.isArray(options) || key === undefined || key === null || key === "") {
        return ""
    }
    const match = options.find((opt) => String(opt?.value) === String(key) || String(opt?.label) === String(key))
    const label = match?.label
    return isFilledString(label) && !isIdLike(label) ? label : ""
}

// Display text for one master cell, including columns that store a referenced
// row uuid. Used when freezing option labels onto a form widget.
const resolveMasterCellDisplayText = (row, field, master, columnKey, masterList = [], lng) => {
    if (!row) return ""
    const localizedRow = resolveRowTranslation(row, master?.fields, lng)
    const rawValue = row[columnKey]
    const localizedValue = localizedRow?.[columnKey]
    if (isFilledString(localizedValue) && !isIdLike(localizedValue)) {
        return localizedValue
    }

    const fromOwn = pickReadableOptionLabel(resolveFieldTranslation(field, lng)?.options, rawValue)
    if (fromOwn) return fromOwn

    if (field?.parentTable) {
        const parent = (Array.isArray(masterList) ? masterList : []).find((item) => item?.dataId === field.parentTable)
        const parentField = parent?.fields?.find((item) => item?.label === (field.mappedColumn || field.label))
        const fromParentOptions = pickReadableOptionLabel(resolveFieldTranslation(parentField, lng)?.options, rawValue)
        if (fromParentOptions) return fromParentOptions
        const parentRow = (Array.isArray(parent?.data) ? parent.data : []).find((item) => String(item?.uuid) === String(rawValue))
        if (parentRow) {
            const parentColumn = field.mappedColumn || parentField?.label
            const parentLocalized = resolveRowTranslation(parentRow, parent?.fields, lng)?.[parentColumn]
            if (isFilledString(parentLocalized) && !isIdLike(parentLocalized)) {
                return parentLocalized
            }
            const parentRaw = parentRow[parentColumn]
            if (isFilledString(parentRaw) && !isIdLike(parentRaw)) {
                return parentRaw
            }
        }
    }

    if (isFilledString(localizedValue)) return localizedValue
    return isFilledString(rawValue) ? rawValue : ""
}

// When a master column is added to a form, options are re-keyed to row uuids.
// The master field's own `translations.options` still use the original option
// ids, so they would not match. Rebuild the bucket: keep the field's text
// translations, and remap each option to the row uuid using row translations
// (text columns), the field's translated option labels, or the referenced
// master when this column points at another table.
export const buildMasterBackedWidgetTranslations = (field, master, columnKey, options = [], masterList = []) => {
    const translations = {};
    const fieldTranslations = isPlainObject(field?.translations) ? field.translations : {};
    const languageSet = new Set(Object.keys(fieldTranslations));
    (Array.isArray(master?.data) ? master.data : []).forEach((row) => {
        Object.keys(isPlainObject(row?.translations) ? row.translations : {}).forEach((lng) => {
            languageSet.add(lng);
        });
    });

    languageSet.forEach((lng) => {
        if (!lng || lng === DEFAULT_LANGUAGE) return;
        const bucket = {};
        const fieldBucket = isPlainObject(fieldTranslations[lng]) ? fieldTranslations[lng] : {};
        FIELD_TEXT_KEYS.forEach((key) => {
            if (typeof fieldBucket[key] === "string" && fieldBucket[key] !== "") {
                bucket[key] = fieldBucket[key];
            }
        });

        if (Array.isArray(options) && options.length > 0) {
            const translatedOptions = [];
            options.forEach((opt) => {
                if (!opt || opt.value === undefined || opt.value === null) return;
                // A reference column's option is keyed by the referenced row's uuid, so
                // the row it was read from is found through the ids carried on the option
                // (or, failing that, by the cell itself).
                const masterRows = Array.isArray(master?.data) ? master.data : [];
                const optionRowUuids = (Array.isArray(opt.rowUuids) ? opt.rowUuids : []).map(String);
                const row = masterRows.find((item) => String(item?.uuid) === String(opt.value))
                    || masterRows.find((item) => optionRowUuids.includes(String(item?.uuid)))
                    || masterRows.find((item) => String(item?.[columnKey]) === String(opt.value));
                if (!row) return;
                const text = resolveMasterCellDisplayText(row, field, master, columnKey, masterList, lng);
                if (isFilledString(text) && text !== opt.label && !isIdLike(text)) {
                    translatedOptions.push({ value: opt.value, label: text });
                }
            });
            if (translatedOptions.length > 0) {
                bucket.options = translatedOptions;
            }
        }

        if (Object.keys(bucket).length > 0) {
            translations[lng] = bucket;
        }
    });

    return Object.keys(translations).length > 0 ? translations : undefined;
};

// Merge a table-column's master-derived translations into the table field's
// `translations[lng].columns` list, matched by column id.
export const mergeTableColumnTranslations = (translations = {}, columnId, columnTranslations) => {
    if (!columnId || !isPlainObject(columnTranslations) || Object.keys(columnTranslations).length === 0) {
        return translations;
    }
    const next = isPlainObject(translations) ? { ...translations } : {};
    Object.entries(columnTranslations).forEach(([lng, bucket]) => {
        if (!lng || lng === DEFAULT_LANGUAGE || !isPlainObject(bucket)) return;
        const prevBucket = isPlainObject(next[lng]) ? next[lng] : {};
        const prevColumns = Array.isArray(prevBucket.columns) ? prevBucket.columns : [];
        next[lng] = {
            ...prevBucket,
            columns: [
                ...prevColumns.filter((col) => String(col?.id) !== String(columnId)),
                { id: columnId, ...bucket },
            ],
        };
    });
    return next;
};

// How many of `rows` carry at least one non-empty translation for `lng`. Drives
// the "12 of 340 translated" hint in the master data toolbar.
export const countTranslatedRows = (rows = [], fields = [], lng) => {
    if (!Array.isArray(rows) || !lng || lng === DEFAULT_LANGUAGE) return 0;
    const hasText = (bucket) =>
        isPlainObject(bucket) &&
        Object.values(bucket).some((value) =>
            isPlainObject(value) ? Object.values(value).some(isFilledString) : isFilledString(value)
        );
    return rows.filter((row) => hasText(row?.translations?.[lng])).length;
};
